import React, { useEffect, useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Shield, ShieldCheck, Calculator, Zap, BellRing, Clock, LineChart, BarChart3, Briefcase, HeartHandshake, Users, CheckCircle2, XCircle, FileCheck2, ShieldOff, KeyRound, Lock } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.5,
      ease: 'easeOut'
    }
  })
};
const SectionHeader = ({
  num,
  eyebrow,
  title,
  subtitle
}: {
  num: string;
  eyebrow?: string;
  title: string;
  subtitle?: string;
}) => <div className="mb-10" data-id="element-2506">
    <div className="flex items-baseline gap-4 mb-3" data-id="element-2507">
      <span className="font-mono text-sm text-red-600" data-id="element-2508">{num}</span>
      {eyebrow && <span className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-2509">
          {eyebrow}
        </span>}
    </div>
    <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-stone-50 leading-tight" data-id="element-2510">
      {title}
    </h2>
    {subtitle && <p className="text-stone-400 text-lg mt-4 max-w-3xl leading-relaxed" data-id="element-2511">
        {subtitle}
      </p>}
  </div>;
export function QuoteSpeedOS() {
  const [activeSection, setActiveSection] = useState('home');
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'how-it-works', 'dashboard', 'guardrails', 'pilot', 'integrations', 'security'];
      const scrollY = window.scrollY + 150;
      for (const section of sections.slice().reverse()) {
        const el = document.getElementById(section);
        if (el && scrollY >= el.offsetTop) {
          setActiveSection(section);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  const subNavItems = [{
    id: 'home',
    label: 'Home'
  }, {
    id: 'how-it-works',
    label: 'How It Works'
  }, {
    id: 'dashboard',
    label: 'Quote Speed Dashboard'
  }, {
    id: 'guardrails',
    label: 'Guardrails'
  }, {
    id: 'pilot',
    label: 'Pilot Plan'
  }, {
    id: 'integrations',
    label: 'Integrations'
  }, {
    id: 'security',
    label: 'Security & Controls'
  }];
  return <div className="min-h-screen bg-stone-950 text-stone-300 font-sans selection:bg-red-600/30" data-id="element-2512">
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 h-16 bg-stone-950/85 backdrop-blur-md border-b border-stone-900 z-50 flex items-center justify-between px-6" data-id="element-2513">
        <Link to="/" className="flex items-center text-stone-400 hover:text-stone-50 transition-colors font-mono text-xs uppercase tracking-widest" data-id="element-2514">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2515" />
          Back to site
        </Link>
        <div className="flex items-center gap-3" data-id="element-2516">
          <div className="w-5 h-5 bg-red-600 flex items-center justify-center" data-id="element-2517">
            <span className="font-serif text-xs font-bold text-white" data-id="element-2518">B</span>
          </div>
          <span className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-2519">
            Quote Speed OS
          </span>
        </div>
      </div>

      {/* Sticky Sub-nav */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-stone-950/90 backdrop-blur-md border-b border-stone-900 overflow-x-auto no-scrollbar" data-id="element-2520">
        <div className="max-w-6xl mx-auto px-6 flex items-center gap-8 h-12" data-id="element-2521">
          {subNavItems.map(item => <button key={item.id} onClick={() => scrollTo(item.id)} className={`font-mono text-xs uppercase tracking-widest whitespace-nowrap transition-colors ${activeSection === item.id ? 'text-stone-50' : 'text-stone-500 hover:text-stone-300'}`} data-id="element-2522">
              {item.label}
            </button>)}
        </div>
      </div>

      {/* SECTION 1 — HOME */}
      <section id="home" className="pt-40 pb-24 border-b border-stone-900" data-id="element-2523">
        <div className="max-w-6xl mx-auto px-6" data-id="element-2524">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-2525">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-stone-800 bg-stone-900/50 font-mono text-xs uppercase tracking-widest text-stone-400 mb-8" data-id="element-2526">
              <Shield className="w-3.5 h-3.5 text-emerald-500" data-id="element-2527" />
              Salesforce-safe by design
            </div>

            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight text-stone-50 leading-[1.05] mb-6 max-w-4xl" data-id="element-2528">
              Cut group quote time from 24 hours to 4 hours.
            </h1>

            <p className="text-xl text-stone-400 leading-relaxed max-w-3xl mb-8" data-id="element-2529">
              Instant, policy-controlled quotes + timed holds — without email
              ping-pong.
            </p>

            <div className="inline-block bg-stone-900/80 border border-stone-800 px-4 py-2 mb-16" data-id="element-2530">
              <p className="font-mono text-sm uppercase tracking-widest text-stone-400" data-id="element-2531">
                AI assists. Pricing is deterministic from rate sheets +
                guardrails.
              </p>
            </div>

            {/* KPI Tiles */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-24" data-id="element-2532">
              <div className="border border-stone-800 bg-stone-900/40 p-6" data-id="element-2533">
                <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-2534">
                  Median time-to-first-quote
                </p>
                <p className="font-mono text-4xl text-stone-50 mb-2" data-id="element-2535">4.0h</p>
                <p className="text-sm text-stone-400" data-id="element-2536">
                  Target: ≤ 4h for in-policy quotes
                </p>
              </div>
              <div className="border border-stone-800 bg-stone-900/40 p-6" data-id="element-2537">
                <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-2538">
                  % within SLA
                </p>
                <p className="font-mono text-4xl text-stone-50 mb-2" data-id="element-2539">94%</p>
                <p className="text-sm text-stone-400" data-id="element-2540">
                  In-policy quotes responded within target
                </p>
              </div>
              <div className="border border-stone-800 bg-stone-900/40 p-6" data-id="element-2541">
                <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-2542">
                  Quote → Hold rate
                </p>
                <p className="font-mono text-4xl text-stone-50 mb-2" data-id="element-2543">47%</p>
                <p className="text-sm text-stone-400" data-id="element-2544">
                  Quotes that progress to held inventory
                </p>
              </div>
            </div>

            {/* 1A: Quote Speed OS */}
            <div className="mb-24" data-id="element-2545">
              <p className="font-mono text-xs uppercase tracking-widest text-red-500 mb-4" data-id="element-2546">
                The Operating System
              </p>
              <h3 className="font-serif text-3xl text-stone-50 mb-8" data-id="element-2547">
                Four modules. One quoting rail.
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2548">
                {[{
                icon: Zap,
                title: 'Deterministic Quote Engine',
                desc: 'Quotes generated from your rate sheets, occupancy rules, and concession matrix. AI structures the request; rules produce the price.'
              }, {
                icon: ShieldCheck,
                title: 'Guardrails + Approvals',
                desc: 'Floors, blackouts, concession limits, approval thresholds. Out-of-policy quotes route for human sign-off automatically.'
              }, {
                icon: Clock,
                title: 'Reminders + SLA Automation',
                desc: 'Hold expiry, follow-up nudges, agency status updates. Nothing falls through the cracks.'
              }, {
                icon: BarChart3,
                title: 'Executive Dashboard',
                desc: 'SLA compliance, funnel metrics, exception rate, blockers list. One screen for the revenue leader.'
              }].map((mod, i) => <div key={i} className="border border-stone-800 bg-stone-900/30 p-6 flex flex-col" data-id="element-2549">
                    <mod.icon className="w-6 h-6 text-red-500 mb-4" data-id="element-2550" />
                    <h4 className="font-serif text-xl text-stone-50 mb-3" data-id="element-2551">
                      {mod.title}
                    </h4>
                    <p className="text-sm text-stone-400 leading-relaxed" data-id="element-2552">
                      {mod.desc}
                    </p>
                  </div>)}
              </div>
            </div>

            {/* 1B: Who it works for */}
            <div className="mb-24" data-id="element-2553">
              <p className="font-mono text-xs uppercase tracking-widest text-red-500 mb-4" data-id="element-2554">
                Entry Paths
              </p>
              <h3 className="font-serif text-3xl text-stone-50 mb-8" data-id="element-2555">
                Who it works for
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2556">
                {[{
                icon: Briefcase,
                title: 'Agency',
                desc: 'Branded portal, instant quote, one-click hold request.'
              }, {
                icon: HeartHandshake,
                title: 'Wedding Planner',
                desc: 'Structured intake form, multi-property comparison, proposal share link.'
              }, {
                icon: Users,
                title: 'Couples / Direct',
                desc: 'Public quote form, instant pricing, no agency intermediation.'
              }].map((path, i) => <div key={i} className="border border-stone-800 bg-stone-900/30 p-6 flex items-start gap-4" data-id="element-2557">
                    <path.icon className="w-5 h-5 text-stone-400 mt-1 flex-shrink-0" data-id="element-2558" />
                    <div data-id="element-2559">
                      <h4 className="font-serif text-lg text-stone-50 mb-2" data-id="element-2560">
                        {path.title}
                      </h4>
                      <p className="text-sm text-stone-400" data-id="element-2561">{path.desc}</p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* 1C: Pilot outcome */}
            <div className="mb-24" data-id="element-2562">
              <p className="font-mono text-xs uppercase tracking-widest text-red-500 mb-4" data-id="element-2563">
                Timeline
              </p>
              <h3 className="font-serif text-3xl text-stone-50 mb-8" data-id="element-2564">
                Pilot outcome
              </h3>
              <div className="relative" data-id="element-2565">
                <div className="hidden md:block absolute top-6 left-0 right-0 h-px border-t border-dashed border-red-900/50 z-0" data-id="element-2566" />
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative z-10" data-id="element-2567">
                  {[{
                  week: 'Week 0',
                  phase: 'Foundation',
                  desc: 'NDA + owners + Drive set up'
                }, {
                  week: 'Week 1',
                  phase: 'Configuration',
                  desc: 'Rates, blackouts, concessions loaded'
                }, {
                  week: 'Week 2',
                  phase: 'Shadow Test',
                  desc: '10 real requests run through the engine'
                }, {
                  week: 'Week 3',
                  phase: 'Go Live',
                  desc: '5 agencies onboarded, live quoting'
                }].map((step, i) => <div key={i} className="border border-stone-800 bg-stone-950 p-6" data-id="element-2568">
                      <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-2" data-id="element-2569">
                        {step.week}
                      </p>
                      <h4 className="font-serif text-xl text-stone-50 mb-3" data-id="element-2570">
                        {step.phase}
                      </h4>
                      <p className="text-sm text-stone-400" data-id="element-2571">{step.desc}</p>
                    </div>)}
                </div>
              </div>
            </div>

            {/* 1D: Salesforce-safe callout */}
            <div className="border border-stone-800 bg-stone-900/40 p-8 flex flex-col md:flex-row gap-6 items-start" data-id="element-2572">
              <div className="w-12 h-12 bg-stone-900 border border-stone-700 flex items-center justify-center flex-shrink-0" data-id="element-2573">
                <Shield className="w-6 h-6 text-emerald-500" data-id="element-2574" />
              </div>
              <div data-id="element-2575">
                <h4 className="font-serif text-2xl text-stone-50 mb-3" data-id="element-2576">
                  Salesforce-safe integration
                </h4>
                <p className="text-stone-400 leading-relaxed max-w-4xl mb-4" data-id="element-2577">
                  BlockQuote is a quoting rail, not a CRM. Salesforce remains
                  your system of record. The integration is minimal: Deal ID,
                  status, timestamps, and a deep link write back to your
                  existing Opportunity. No pricing logic in Salesforce, no
                  schema changes required, fully reversible.
                </p>
                <Link to="/salesforce" className="text-sm font-mono uppercase tracking-widest text-stone-300 hover:text-stone-50 transition-colors inline-flex items-center gap-2" data-id="element-2578">
                  View integration spec <ArrowRight className="w-4 h-4" data-id="element-2579" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2 — HOW IT WORKS */}
      <section id="how-it-works" className="py-24 border-b border-stone-900" data-id="element-2580">
        <div className="max-w-6xl mx-auto px-6" data-id="element-2581">
          <SectionHeader num="02" eyebrow="The Pipeline" title="From request to audited proposal in six steps." data-id="element-2582" />

          <div className="flex flex-col md:flex-row gap-4 mt-12" data-id="element-2583">
            {[{
            num: '1',
            title: 'Request',
            desc: 'Email, form, or WhatsApp. Any inbound channel.'
          }, {
            num: '2',
            title: 'Structured Intake',
            desc: 'AI extracts party size, dates, room mix, must-haves into a clean RFP object.'
          }, {
            num: '3',
            title: 'Quotes',
            desc: 'Rules engine produces Good/Better/Best options against rate sheets.'
          }, {
            num: '4',
            title: 'Guardrails / Approvals',
            desc: 'Out-of-policy options route to the approver. In-policy options ship immediately.'
          }, {
            num: '5',
            title: 'Holds',
            desc: 'Timed inventory holds with automatic expiry and follow-up.'
          }, {
            num: '6',
            title: 'Proposal + Audit + KPIs',
            desc: 'Branded proposal link. Every step logged. KPIs updated in real time.'
          }].map((step, i) => <Fragment key={i} data-id="element-2584">
                <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} custom={i} className="flex-1 border border-stone-800 bg-stone-900/30 p-5 relative" data-id="element-2585">
                  <p className="font-mono text-xs text-red-500 mb-3" data-id="element-2586">
                    0{step.num}
                  </p>
                  <h4 className="font-serif text-lg text-stone-50 mb-2" data-id="element-2587">
                    {step.title}
                  </h4>
                  <p className="text-xs text-stone-400 leading-relaxed" data-id="element-2588">
                    {step.desc}
                  </p>
                </motion.div>
                {i < 5 && <div className="hidden md:flex items-center justify-center text-stone-700" data-id="element-2589">
                    <ArrowRight className="w-4 h-4" data-id="element-2590" />
                  </div>}
              </Fragment>)}
          </div>
          <div className="mt-8 text-center" data-id="element-2591">
            <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-2592">
              AI assists. Pricing is deterministic.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3 — QUOTE SPEED DASHBOARD */}
      <section id="dashboard" className="py-24 border-b border-stone-900" data-id="element-2593">
        <div className="max-w-6xl mx-auto px-6" data-id="element-2594">
          <SectionHeader num="03" eyebrow="Operator View" title="The dashboard your revenue leader actually opens." subtitle="One screen. Five signals. Built for the revenue leader who needs to know what's slipping and where the time is going." data-id="element-2595" />

          <div className="space-y-16 mt-16" data-id="element-2596">
            {/* Panel 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-2597">
              <div className="border border-stone-800 bg-stone-900/50 h-64 flex items-center justify-center p-8" data-id="element-2598">
                <div className="w-32 h-32 rounded-full border-[16px] border-stone-800 relative flex items-center justify-center" data-id="element-2599">
                  <div className="absolute inset-[-16px] rounded-full border-[16px] border-emerald-500" style={{
                  clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 50%, 50% 50%, 50% 0, 0 0)'
                }} data-id="element-2600"></div>
                  <span className="font-mono text-xl text-stone-50" data-id="element-2601">94%</span>
                </div>
              </div>
              <div data-id="element-2602">
                <h4 className="font-serif text-2xl text-stone-50 mb-4" data-id="element-2603">
                  SLA Compliance
                </h4>
                <p className="text-stone-400 leading-relaxed" data-id="element-2604">
                  Donut showing % of quotes hitting the 4-hour target this week
                  vs last. Instantly see if the team is keeping up with inbound
                  volume.
                </p>
              </div>
            </div>

            {/* Panel 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-2605">
              <div className="order-2 md:order-1" data-id="element-2606">
                <h4 className="font-serif text-2xl text-stone-50 mb-4" data-id="element-2607">
                  Median Time-to-First-Quote
                </h4>
                <p className="text-stone-400 leading-relaxed" data-id="element-2608">
                  Trend line over the last 30/90 days with the SLA threshold
                  marked. Track velocity improvements as the pilot scales.
                </p>
              </div>
              <div className="order-1 md:order-2 border border-stone-800 bg-stone-900/50 h-64 flex flex-col justify-end p-8 relative" data-id="element-2609">
                <div className="absolute top-1/2 left-0 right-0 border-t border-dashed border-red-500/50" data-id="element-2610"></div>
                <div className="flex items-end justify-between h-32 gap-2" data-id="element-2611">
                  {[40, 35, 45, 30, 25, 20, 15].map((h, i) => <div key={i} className="w-full bg-stone-700" style={{
                  height: `${h}%`
                }} data-id="element-2612"></div>)}
                </div>
              </div>
            </div>

            {/* Panel 3 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-2613">
              <div className="border border-stone-800 bg-stone-900/50 h-64 flex flex-col justify-center p-8 gap-4" data-id="element-2614">
                <div className="w-full h-8 flex" data-id="element-2615">
                  <div className="h-full bg-emerald-500/80 w-[70%]" data-id="element-2616"></div>
                  <div className="h-full bg-amber-500/80 w-[20%]" data-id="element-2617"></div>
                  <div className="h-full bg-red-500/80 w-[10%]" data-id="element-2618"></div>
                </div>
                <div className="flex justify-between text-xs font-mono text-stone-500" data-id="element-2619">
                  <span data-id="element-2620">In-Policy (70%)</span>
                  <span data-id="element-2621">Approval Req (20%)</span>
                  <span data-id="element-2622">Blocked (10%)</span>
                </div>
              </div>
              <div data-id="element-2623">
                <h4 className="font-serif text-2xl text-stone-50 mb-4" data-id="element-2624">
                  Exception Rate
                </h4>
                <p className="text-stone-400 leading-relaxed" data-id="element-2625">
                  Stacked bar of in-policy vs approval-required vs blocked.
                  Understand how often your guardrails are triggering manual
                  intervention.
                </p>
              </div>
            </div>

            {/* Panel 4 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-2626">
              <div className="order-2 md:order-1" data-id="element-2627">
                <h4 className="font-serif text-2xl text-stone-50 mb-4" data-id="element-2628">
                  Funnel: Quote → Hold → Contract
                </h4>
                <p className="text-stone-400 leading-relaxed" data-id="element-2629">
                  Conversion at each stage with drop-off reasons. See exactly
                  where deals are stalling in the pipeline.
                </p>
              </div>
              <div className="order-1 md:order-2 border border-stone-800 bg-stone-900/50 h-64 flex flex-col items-center justify-center p-8 gap-2" data-id="element-2630">
                <div className="w-full h-12 bg-stone-800 flex items-center justify-center font-mono text-xs text-stone-400" data-id="element-2631">
                  Quote (100%)
                </div>
                <div className="w-[75%] h-12 bg-stone-700 flex items-center justify-center font-mono text-xs text-stone-300" data-id="element-2632">
                  Hold (75%)
                </div>
                <div className="w-[50%] h-12 bg-stone-600 flex items-center justify-center font-mono text-xs text-stone-200" data-id="element-2633">
                  Contract (50%)
                </div>
              </div>
            </div>

            {/* Panel 5 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-2634">
              <div className="border border-stone-800 bg-stone-900/50 h-64 flex flex-col p-6 gap-3 overflow-hidden" data-id="element-2635">
                {[{
                text: 'Missing dates for Smith Wedding',
                status: 'Missing Data',
                color: 'bg-stone-500'
              }, {
                text: 'Concession override pending',
                status: 'Approval Stuck',
                color: 'bg-amber-500'
              }, {
                text: 'Hold expires in 2 hours',
                status: 'Expiring Soon',
                color: 'bg-red-500'
              }, {
                text: 'Missing room mix for TechCorp',
                status: 'Missing Data',
                color: 'bg-stone-500'
              }].map((item, i) => <div key={i} className="flex items-center justify-between border-b border-stone-800 pb-3" data-id="element-2636">
                    <span className="text-sm text-stone-300" data-id="element-2637">{item.text}</span>
                    <span className={`text-[10px] font-mono uppercase px-2 py-1 text-white ${item.color}`} data-id="element-2638">
                      {item.status}
                    </span>
                  </div>)}
              </div>
              <div data-id="element-2639">
                <h4 className="font-serif text-2xl text-stone-50 mb-4" data-id="element-2640">
                  Blockers List
                </h4>
                <p className="text-stone-400 leading-relaxed" data-id="element-2641">
                  Live list: missing data, approvals stuck, holds about to
                  expire. Actionable items to unblock revenue immediately.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — GUARDRAILS */}
      <section id="guardrails" className="py-24 border-b border-stone-900" data-id="element-2642">
        <div className="max-w-6xl mx-auto px-6" data-id="element-2643">
          <SectionHeader num="04" eyebrow="Policy Engine" title="Pricing is rule-based. AI never invents a rate." subtitle="The system says yes when policy allows yes and routes to a human when it doesn't." data-id="element-2644" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12" data-id="element-2645">
            {/* Allowed */}
            <div className="border border-emerald-900/30 bg-emerald-950/10 p-8" data-id="element-2646">
              <h4 className="font-mono text-sm uppercase tracking-widest text-emerald-500 mb-6 flex items-center gap-2" data-id="element-2647">
                <CheckCircle2 className="w-4 h-4" data-id="element-2648" /> Allowed
              </h4>
              <ul className="space-y-4" data-id="element-2649">
                {['Quotes within rate floor and concession limits', 'Date ranges outside blackout windows', 'Auto-approval when all guardrails pass', 'Deterministic Good/Better/Best generation', 'Multi-property comparison within policy', 'Audit trail on every option'].map((item, i) => <li key={i} className="flex items-start gap-3 text-stone-300" data-id="element-2650">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-2651" />
                    <span data-id="element-2652">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* Not Allowed */}
            <div className="border border-stone-800 bg-stone-900/30 p-8" data-id="element-2653">
              <h4 className="font-mono text-sm uppercase tracking-widest text-stone-500 mb-6 flex items-center gap-2" data-id="element-2654">
                <XCircle className="w-4 h-4" data-id="element-2655" /> Not Allowed
              </h4>
              <ul className="space-y-4" data-id="element-2656">
                {['AI inventing or estimating rates', 'Quoting outside loaded rate sheets', 'Concessions above defined caps without approval', 'Hold extensions beyond policy', 'Bypassing approver for out-of-policy options', 'Silent overrides (every exception is logged)'].map((item, i) => <li key={i} className="flex items-start gap-3 text-stone-400" data-id="element-2657">
                    <XCircle className="w-5 h-5 text-stone-600 flex-shrink-0 mt-0.5" data-id="element-2658" />
                    <span data-id="element-2659">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="mt-12 border border-stone-800 bg-stone-900/50 p-6 text-center" data-id="element-2660">
            <p className="text-stone-300" data-id="element-2661">
              Every exception is logged. Who approved it, when, against which
              rule. The audit log is queryable for any quote.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 5 — PILOT PLAN */}
      <section id="pilot" className="py-24 border-b border-stone-900" data-id="element-2662">
        <div className="max-w-6xl mx-auto px-6" data-id="element-2663">
          <SectionHeader num="05" eyebrow="21-Day Pilot" title="Week-by-week pilot plan." data-id="element-2664" />

          <div className="mt-12 max-w-3xl" data-id="element-2665">
            <div className="space-y-12 border-l border-red-900/50 ml-3 pl-8 py-2" data-id="element-2666">
              {[{
              week: 'Week 0',
              title: 'Foundation',
              desc: 'NDA signed · Pilot owners assigned · Shared Drive set up · Stakeholder kickoff'
            }, {
              week: 'Week 1',
              title: 'Configuration',
              desc: 'Rate sheets loaded · Blackout windows defined · Concession matrix imported · Approval thresholds set'
            }, {
              week: 'Week 2',
              title: 'Shadow Test',
              desc: '10 real requests processed in shadow · Quote outputs compared to manual · Tuning pass'
            }, {
              week: 'Week 3',
              title: 'Go Live',
              desc: '5 agencies onboarded · Live quoting active · Daily KPI review'
            }].map((step, i) => <div key={i} className="relative" data-id="element-2667">
                  <div className="absolute -left-[41px] top-1 w-4 h-4 rounded-full bg-stone-950 border-2 border-red-500" data-id="element-2668" />
                  <p className="font-mono text-xs uppercase tracking-widest text-red-500 mb-2" data-id="element-2669">
                    {step.week}
                  </p>
                  <h4 className="font-serif text-2xl text-stone-50 mb-3" data-id="element-2670">
                    {step.title}
                  </h4>
                  <p className="text-stone-400 leading-relaxed" data-id="element-2671">
                    Deliverables: {step.desc}
                  </p>
                </div>)}
            </div>
          </div>

          <div className="mt-16 border border-red-900/40 bg-red-950/10 p-8 max-w-3xl" data-id="element-2672">
            <h4 className="font-serif text-xl text-stone-50 mb-2 flex items-center gap-3" data-id="element-2673">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" data-id="element-2674" /> Success
              Criteria
            </h4>
            <p className="text-stone-300" data-id="element-2675">
              Pilot succeeds if the 4-hour target is hit for in-policy quotes by
              end of Week 3.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 6 — INTEGRATIONS */}
      <section id="integrations" className="py-24 border-b border-stone-900" data-id="element-2676">
        <div className="max-w-6xl mx-auto px-6" data-id="element-2677">
          <SectionHeader num="06" eyebrow="Salesforce-safe" title="Salesforce stays system-of-record." subtitle="BlockQuote is the quoting rail. Salesforce is the source of truth. We sync the minimum needed to keep your pipeline accurate." data-id="element-2678" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12" data-id="element-2679">
            <div className="border border-stone-800 bg-stone-900/30 p-6" data-id="element-2680">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-2681">
                What we sync
              </p>
              <p className="text-sm text-stone-300 leading-relaxed" data-id="element-2682">
                Deal ID, status (Submitted → Quoted → Hold → Contracted → Closed
                Won/Lost), timestamps, deep link to BlockQuote.
              </p>
            </div>
            <div className="border border-stone-800 bg-stone-900/30 p-6" data-id="element-2683">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-2684">
                What we don't do
              </p>
              <p className="text-sm text-stone-300 leading-relaxed" data-id="element-2685">
                No pricing logic in Salesforce. No schema changes required. No
                takeover of pipeline ownership.
              </p>
            </div>
            <div className="border border-stone-800 bg-stone-900/30 p-6" data-id="element-2686">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-2687">
                How it installs
              </p>
              <p className="text-sm text-stone-300 leading-relaxed" data-id="element-2688">
                Three modes: Link + status sync (days), custom object (weeks),
                or middleware (enterprise).
              </p>
            </div>
          </div>

          <div className="mt-10" data-id="element-2689">
            <Link to="/salesforce" className="font-mono text-sm uppercase tracking-widest text-stone-300 hover:text-stone-50 transition-colors inline-flex items-center gap-2" data-id="element-2690">
              See full integration spec <ArrowRight className="w-4 h-4" data-id="element-2691" />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 7 — SECURITY */}
      <section id="security" className="py-24" data-id="element-2692">
        <div className="max-w-6xl mx-auto px-6" data-id="element-2693">
          <SectionHeader num="07" eyebrow="Security" title="Built for the security review." subtitle="Tenant isolation, role-based access, audit logs, AI usage controls. The pilot is approvable." data-id="element-2694" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12" data-id="element-2695">
            {[{
            icon: Lock,
            title: 'Tenant Isolation',
            desc: 'Each resort is a fully isolated tenant. No cross-tenant access at any layer.'
          }, {
            icon: Users,
            title: 'RLS Roles',
            desc: 'Row-level security. Resort Admin, Resort Staff, Agency Admin, Agency Agent, Super Admin.'
          }, {
            icon: FileCheck2,
            title: 'Audit Logs',
            desc: 'Every quote, status change, approval, and sync event logged with user, timestamp, and diff.'
          }, {
            icon: ShieldOff,
            title: 'No Sensitive PII',
            desc: 'We only ingest the minimum needed to quote. No passports, no payment data, no health info.'
          }, {
            icon: Shield,
            title: 'AI Usage Controls',
            desc: 'AI is scoped to intake parsing and follow-up drafting. AI cannot price, approve, or sync to Salesforce.'
          }, {
            icon: KeyRound,
            title: 'OAuth + Scoped Permissions',
            desc: 'Minimum-scope OAuth for any external integration. Revocable in seconds.'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} custom={i} className="border border-stone-800 bg-stone-900/30 p-6 flex items-start gap-4" data-id="element-2696">
                <item.icon className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-2697" />
                <div data-id="element-2698">
                  <h4 className="font-serif text-xl text-stone-50 mb-2" data-id="element-2699">
                    {item.title}
                  </h4>
                  <p className="text-sm text-stone-400 leading-relaxed" data-id="element-2700">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CLOSING STRIP */}
      <section className="border-t border-stone-900 bg-gradient-to-b from-stone-950 to-red-950/20 py-24" data-id="element-2701">
        <div className="max-w-6xl mx-auto px-6 text-center" data-id="element-2702">
          <p className="font-mono text-xs uppercase tracking-widest text-red-500 mb-6" data-id="element-2703">
            The Mandate
          </p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 leading-tight mb-6 max-w-3xl mx-auto" data-id="element-2704">
            Hit the 4-hour quote target without disrupting Salesforce.
          </h2>
          <p className="text-lg text-stone-400 max-w-2xl mx-auto" data-id="element-2705">
            Deterministic quoting, policy guardrails, audited holds — the
            operating layer your revenue team needs.
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-stone-900 py-10" data-id="element-2706">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-2707">
          <p className="font-mono text-xs text-stone-600" data-id="element-2708">
            © 2026 BlockQuote. Salesforce-safe by design.
          </p>
          <Link to="/" className="font-mono text-xs uppercase tracking-widest text-stone-500 hover:text-stone-300 transition-colors flex items-center gap-2" data-id="element-2709">
            <ArrowLeft className="w-3.5 h-3.5" data-id="element-2710" />
            Back to site
          </Link>
        </div>
      </footer>
    </div>;
}